import { Box, Button } from "@mui/material";
import React, { useRef } from "react";
import Select, { components } from "react-select";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import "./view-campaign-roi.scss";
import Divider from "@mui/material/Divider";
import html2canvas from "html2canvas";

interface CampaignMetricsData {
  metricsData: any;
  isLoading?: boolean;
  exportChart?: (params: any, data?: any, base64Image?: any) => void; // Optional exportChart function
  enableExport?: boolean;
}

const statusColors: { [key: string]: string } = {
  High: "#8E44AD",
  Moderate: "#6488EA",
  Low: "#BD3D44",
  "Not Available": "#B0BEC5",
};
const ViewCampaignRoi: React.FC<CampaignMetricsData> = ({
  metricsData,
  exportChart,
  enableExport,
}) => {
  const [format, setFormat] = React.useState({ value: "PDF", label: "PDF" });

  const campaignsROI = metricsData?.campaignsROI || [];
  const divToCaptureRef = useRef(null);
  const exportDivAsImageBase64 = async () => {
    if (!divToCaptureRef.current) {
      console.error("Div element not found to capture.");
      return;
    }

    try {
      // Use html2canvas to render the div to a canvas
      const canvas = await html2canvas(divToCaptureRef.current, {
        scale: 2, // Increase scale for better quality image
        useCORS: true, // Important if your div has images from other origins
      });
      const imgURI = canvas.toDataURL("image/png");
      const base64Data = imgURI.split(",")[1];
      return base64Data;
    } catch (error) {
      console.error("Error capturing div as image:", error);
      alert("Error capturing div as image. Check console for details.");
    }
  };

  const handleExport = async () => {
    if (!exportChart) return;

    const params = {
      metrics: "campaignsROI",
      format: format?.value?.toLowerCase(),
    };

    const base64Image = await exportDivAsImageBase64();
    exportChart(
      params,
      format.value === "PDF" ? params?.metrics : undefined,
      format.value === "PDF" ? base64Image : undefined
    );
  };
  return (
    <Box className="roi-wrapper">
      <div className="view-campaign-roi-wrapper">
        <Box
          sx={{
            padding: "10px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <p className="header-text">Campaign ROI (%)</p>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Select
              options={[
                { value: "PDF", label: "PDF" },
                { value: "CSV", label: "CSV" },
              ]}
              isSearchable={false}
              value={format}
              onChange={(selectedOption: any) => setFormat(selectedOption)}
              className="select-dropdown"
              components={{
                DropdownIndicator: (props) => {
                  return (
                    <components.DropdownIndicator {...props}>
                      <ArrowDropDownIcon
                        style={{ fontSize: 20, color: "black" }}
                      />
                    </components.DropdownIndicator>
                  );
                },
                IndicatorSeparator: () => null, // ⛔️ removes vertical line
                // DropdownIndicator, // ✅ custom icon
              }}
            />
            <Button
              variant="outlined"
              onClick={handleExport}
              className="address-edit-button"
              disabled={!enableExport}
            >
              Export
            </Button>
          </Box>
        </Box>

        <div
          ref={divToCaptureRef}
          style={{
            position: "absolute",
            left: "-9999px",
            top: "-9999px",
            width: "800px",
            overflow: "hidden",
          }}
        >
          <Box className="chart-wrapper">
            {campaignsROI?.length ? (
              <>
                <Box className="chart-label-wrapper" gap={1}>
                  {campaignsROI?.map((campaign: any, index: number) => (
                    <Box key={index}>{campaign.name}</Box>
                  ))}
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box className="chart-value-wrapper" gap={1}>
                  {campaignsROI?.map((campaign: any, index: number) => (
                    <Box
                      className="button-wrapper"
                      sx={{ background: statusColors[campaign.roiCategory] }}
                      key={index}
                    >
                      {campaign?.roiPercentage ? campaign.roiPercentage : "N/A"}
                    </Box>
                  ))}
                </Box>
              </>
            ) : (
              <Box className="no-data-found">
                <p
                  style={{
                    textAlign: "center",
                    fontSize: "18px",
                    color: "#7B8A8B",
                    fontFamily: "Roboto, sans-serif",
                    fontWeight: 400,
                    margin: "40px 0",
                    width: "100%",
                    height: "100%",
                  }}
                >
                  No data available
                </p>
              </Box>
            )}
          </Box>
          <Box className="chart-footer">
            <Box className="chart-footer-item">
              <Box
                className="chart-footer-item-color"
                style={{ background: "#8E44AD" }}
              ></Box>
              <Box className="chart-footer-item-text">High</Box>
            </Box>
            <Box className="chart-footer-item">
              <Box
                className="chart-footer-item-color"
                style={{ background: "#6488EA" }}
              ></Box>
              <Box className="chart-footer-item-text">Moderate</Box>
            </Box>
            <Box className="chart-footer-item">
              <Box
                className="chart-footer-item-color"
                style={{ background: "#BD3D44" }}
              ></Box>
              <Box className="chart-footer-item-text">Low</Box>
            </Box>
          </Box>
        </div>
        <Box>
          <Box className="chart-wrapper">
            {campaignsROI?.slice(0, 5)?.length ? (
              <>
                <Box className="chart-label-wrapper" gap={1}>
                  {campaignsROI
                    ?.slice(0, 5)
                    ?.map((campaign: any, index: number) => (
                      <Box key={index}>{campaign.name}</Box>
                    ))}
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box className="chart-value-wrapper" gap={1}>
                  {campaignsROI
                    ?.slice(0, 5)
                    ?.map((campaign: any, index: number) => (
                      <Box
                        className="button-wrapper"
                        sx={{ background: statusColors[campaign.roiCategory] }}
                        key={index}
                      >
                        {campaign?.roiPercentage
                          ? campaign.roiPercentage
                          : "N/A"}
                      </Box>
                    ))}
                </Box>
              </>
            ) : (
              <Box className="no-data-found">
                <p
                  style={{
                    textAlign: "center",
                    fontSize: "18px",
                    color: "#7B8A8B",
                    fontFamily: "Roboto, sans-serif",
                    fontWeight: 400,
                    margin: "40px 0",
                    width: "100%",
                    height: "100%",
                  }}
                >
                  No data available
                </p>
              </Box>
            )}
          </Box>
          <Box className="chart-footer">
            <Box className="chart-footer-item">
              <Box
                className="chart-footer-item-color"
                style={{ background: "#8E44AD" }}
              ></Box>
              <Box className="chart-footer-item-text">High</Box>
            </Box>
            <Box className="chart-footer-item">
              <Box
                className="chart-footer-item-color"
                style={{ background: "#6488EA" }}
              ></Box>
              <Box className="chart-footer-item-text">Moderate</Box>
            </Box>
            <Box className="chart-footer-item">
              <Box
                className="chart-footer-item-color"
                style={{ background: "#BD3D44" }}
              ></Box>
              <Box className="chart-footer-item-text">Low</Box>
            </Box>
          </Box>
        </Box>
      </div>
    </Box>
  );
};

export default ViewCampaignRoi;
